/**
 * What an installed app does on launch: open-or-focus its start tab.
 *
 * Runs from `main.tsx`, before React and before routing, so the store already
 * holds the right active tab when `WorkspaceUrlSync` mounts and mirrors the URL
 * onto it. Done in an effect instead, the bar's URL→tab write would land on
 * the OLD active tab in the same commit — the very overwrite this avoids.
 *
 * The URL is then rewritten to that tab's own path, because the bar mirrors the
 * landing URL onto whatever tab is focused — and the manifest's `start_url` is
 * the bare destination (`/tasks`). Without the rewrite a tab parked on
 * `/tasks?list=x` would be flattened back to `/tasks` on every launch, losing
 * the one piece of state the tab existed to hold.
 */
import { startTabFor } from '@shared/app-identity/appNav';

import { useWorkspaceStore } from './stores/useWorkspaceStore';

import type { AppSlug } from '@shared/app-identity/installedApp';

/** The focused primary tab's stored path, or null when there isn't one. */
function activePrimaryPath(): string | null {
  const { tabs, primaryActiveTabId } = useWorkspaceStore.getState();
  return tabs.find(t => t.id === primaryActiveTabId)?.path ?? null;
}

/**
 * Point the browser at `path` without a navigation, preserving history state.
 *
 * `replaceState` rather than a router navigate: this runs before React exists,
 * and the router reads `window.location` when it mounts. The app has done this
 * once already at this point — `captureInstalledApp` strips the `?app=` marker
 * the same way, and preserves `history.state` for the same reason.
 *
 * A landing URL that still carries a query after the marker is stripped is NOT
 * a plain launch and is left alone: it is an OAuth return (`?token=`, read by
 * `App.tsx` at first render — dropping it would boot the app signed out) or a
 * deep link, and either one outranks a remembered tab.
 */
function rewriteUrl(path: string): void {
  if (window.location.search !== '') return;
  if (path === window.location.pathname) return;
  window.history.replaceState(window.history.state, '', path);
}

/**
 * Open-or-focus `slug`'s start destination in the primary group, then land the
 * browser on that tab's own path so the tab bar's URL mirror is a no-op.
 */
export function openStartTab(slug: AppSlug): void {
  useWorkspaceStore.getState().openOrFocusDestination(startTabFor(slug));
  const path = activePrimaryPath();
  if (path) rewriteUrl(path);
}
